'use client'

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react'
import type { HRCase, CaseStatus, CaseTier } from '../types/case.types'
import {
  mockCases,
  mockCaseDrivers,
  AUTHORED_AVG_RESOLUTION_MINUTES,
} from '../mocks/cases.mock'
import type { QueueMetrics, CaseDriver } from '../types/case.types'
import { fetchAutoRuns } from '@/hooks/useAutoRuns'
import { fetchRampCases, prefetchRampAutoTrail, RAMP_CASES_PAGE_SIZE } from '@/hooks/useRampCases'
import { runsToCases } from '../utils/runToCase'
import { computeQueueMetrics } from '../utils/case.utils'
import { publishCaseSnapshot } from '@/lib/ai-context-builder'
import { isForbidden } from '@/lib/api-client'

// ── State shape ────────────────────────────────────────────────────────────

interface CaseFilters {
  status: CaseStatus | 'all'
  tier: CaseTier | 'all'
  search: string
}

interface CaseStoreState {
  cases: HRCase[]
  metrics: QueueMetrics
  drivers: CaseDriver[]
  selectedCaseId: string | null
  filters: CaseFilters
  activeTab: string
}

interface CaseStoreActions {
  selectCase: (id: string | null) => void
  setFilter: (key: keyof CaseFilters, value: string) => void
  resetFilters: () => void
  setActiveTab: (tab: string) => void
  resolveCase: (id: string, summary: string) => void
  addWorkbenchNote: (id: string, note: string) => void
  addCase: (c: HRCase) => void
  /**
   * Pull the live ramp cases again, now.
   *
   * The poll below is a ten second heartbeat, which is right for a queue nobody
   * is touching and far too slow for the half second after a specialist presses
   * Approve. The approval panel and the chat composer call this so the panel they
   * are standing in front of redraws off the server rather than off hope.
   */
  refreshRampCases: () => Promise<void>
}

type CaseStoreContextType = CaseStoreState & CaseStoreActions

// ── Context ────────────────────────────────────────────────────────────────

const CaseStoreContext = createContext<CaseStoreContextType | undefined>(undefined)

const DEFAULT_FILTERS: CaseFilters = { status: 'all', tier: 'all', search: '' }

export function CaseStoreProvider({ children }: { children: ReactNode }) {
  const [cases, setCases] = useState<HRCase[]>(mockCases)
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null)
  const [filters, setFilters] = useState<CaseFilters>(DEFAULT_FILTERS)
  const [activeTab, setActiveTab] = useState('dashboard')

  /**
   * The one-way bridge from the Auto Runs API, plan 05 section 5.
   *
   * Real runs are read once on mount and merged AHEAD of the mocks, so a visitor
   * opening the Live Queue straight after the HR Auto demo finds that run at the
   * top of it. Nothing writes back: this store stays frontend-only and the
   * existing demo beat it powers is untouched.
   *
   * A failure is swallowed on purpose. The queue keeps its mocks and shows no
   * error line, no empty state and no retry, because the one thing this screen
   * must never do on a stand is display a stack of red where the backlog should
   * be. The console line is for whoever is debugging it afterwards.
   */
  useEffect(() => {
    let cancelled = false

    fetchAutoRuns(10)
      .then((runs) => {
        if (cancelled || runs.length === 0) return
        const imported = runsToCases(runs)
        setCases((prev) => {
          const importedIds = new Set(imported.map((c) => c.id))
          return [...imported, ...prev.filter((c) => !importedIds.has(c.id))]
        })
      })
      .catch((err) => {
        // Deliberately silent in the UI. See above.
        console.warn('[caseStore] Auto Runs unavailable; the Live Queue is showing mocks only.', err)
      })

    return () => {
      cancelled = true
    }
  }, [])

  /**
   * The live Supervity Auto ramp, `docs/wiring/01-the-contract.md`.
   *
   * Same shape as the Auto Runs bridge directly above, with two differences.
   *
   * It REPLACES the live rows on every poll rather than merging them, because a
   * ramp case is not a snapshot: its audit trail grows a step at a time while the
   * room watches, so the newer copy must win outright. Every mock survives the
   * filter, and `mockCases` is never touched.
   *
   * And it polls, on a ten second heartbeat, which is the first polling loop in
   * this frontend. There is no `refetch`-on-focus idiom here to borrow and a ramp
   * arrives while the page is already open.
   */
  const applyRampCases = useCallback((live: HRCase[]) => {
    if (live.length === 0) return
    setCases((prev) => {
      const ids = new Set(live.map((c) => c.id))
      return [...live, ...prev.filter((c) => !ids.has(c.id))]
    })
  }, [])

  /**
   * One warning, not one every ten seconds.
   *
   * A backend that is not up yet is the expected state of this page for most of
   * its life, and a console filling at six lines a minute buries the one line
   * whoever is debugging it actually needs.
   */
  const rampWarnedRef = useRef(false)

  const warnRampUnavailable = useCallback((err: unknown) => {
    if (rampWarnedRef.current) return
    rampWarnedRef.current = true
    // Deliberately silent in the UI, exactly like the Auto Runs bridge above.
    if (isForbidden(err)) {
      console.warn(
        '[caseStore] Ramp cases refused (403): this login carries no admin/user role. Polling stopped.',
        err
      )
      return
    }
    console.warn('[caseStore] Ramp cases unavailable; the Live Queue is showing mocks only.', err)
  }, [])

  const refreshRampCases = useCallback(async () => {
    try {
      applyRampCases(await fetchRampCases(RAMP_CASES_PAGE_SIZE))
    } catch (err) {
      warnRampUnavailable(err)
    }
  }, [applyRampCases, warnRampUnavailable])

  useEffect(() => {
    let cancelled = false
    let id: ReturnType<typeof setInterval> | undefined

    const load = () => {
      fetchRampCases(RAMP_CASES_PAGE_SIZE)
        .then((live) => {
          if (cancelled) return
          applyRampCases(live)
          // Warm the trail of the newest launch while the person is still
          // reading the queue. Opening its Audit trail is what they do next,
          // and the first read of one costs about four seconds. One case, not
          // all of them: each trail is about a megabyte.
          prefetchRampAutoTrail(live[0]?.caseRef)
        })
        .catch((err) => {
          warnRampUnavailable(err)
          // A backend that is down may come back; a login with no role will
          // not grow one between ticks. Stop the heartbeat on a 403.
          if (isForbidden(err) && id !== undefined) {
            clearInterval(id)
            id = undefined
          }
        })
    }

    load()
    id = setInterval(load, 4_000)

    return () => {
      cancelled = true
      if (id !== undefined) clearInterval(id)
    }
  }, [applyRampCases, warnRampUnavailable])

  /**
   * Hand the current list to the AI Manager's context builder.
   *
   * That builder is a plain module and cannot read this context, so it counted
   * the mock array instead and the copilot told the room there were sixteen
   * cases while the queue behind it showed twenty-one, imported runs included.
   * Publishing the list here is the same pattern the pages use for the focused
   * entity, and it means the copilot's counts come off the list the screen is
   * rendering. Cleared on unmount so nothing stale outlives the provider.
   */
  useEffect(() => {
    publishCaseSnapshot(cases)
    return () => publishCaseSnapshot(null)
  }, [cases])

  /**
   * The KPI band, off the same cases the queue is listing.
   *
   * It used to be a module-level snapshot of the mock array, so the dashboard
   * described sixteen mock cases while the queue two tabs away described
   * twenty-one, imported runs included, and the two counted "open" by different
   * rules on top of that. One function, one list, one answer.
   */
  const metrics = useMemo(
    () =>
      computeQueueMetrics(cases, {
        avgResolutionMinutes: AUTHORED_AVG_RESOLUTION_MINUTES,
      }),
    [cases]
  )

  const selectCase = useCallback((id: string | null) => setSelectedCaseId(id), [])

  const setFilter = useCallback((key: keyof CaseFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }, [])

  const resetFilters = useCallback(() => setFilters(DEFAULT_FILTERS), [])

  const resolveCase = useCallback((id: string, summary: string) => {
    setCases((prev) =>
      prev.map((c) =>
        c.id === id
          ? {
              ...c,
              status: 'resolved' as CaseStatus,
              resolvedAt: new Date().toISOString(),
              resolutionSummary: summary,
            }
          : c
      )
    )
  }, [])

  const addWorkbenchNote = useCallback((id: string, note: string) => {
    setCases((prev) =>
      prev.map((c) => (c.id === id ? { ...c, workbenchNote: note } : c))
    )
  }, [])

  const addCase = useCallback((newCase: HRCase) => {
    setCases((prev) => {
      if (prev.some((c) => c.id === newCase.id)) return prev
      return [newCase, ...prev]
    })
  }, [])

  return (
    <CaseStoreContext.Provider
      value={{
        cases,
        metrics,
        drivers: mockCaseDrivers,
        selectedCaseId,
        filters,
        activeTab,
        selectCase,
        setFilter,
        resetFilters,
        setActiveTab,
        resolveCase,
        addWorkbenchNote,
        addCase,
        refreshRampCases,
      }}
    >
      {children}
    </CaseStoreContext.Provider>
  )
}

export function useCaseStore(): CaseStoreContextType | null {
  return useContext(CaseStoreContext) ?? null
}
